import { useMemo } from "react";
import useStyles from "./styles";
import Avatar from "@material-ui/core/Avatar";
import { clsx } from "modules/Utils";
import TableElement, { ColDef } from "elements/TableElement";

type ImportFansItem = {
    fullName: string;
    city: string;
    state: string;
    country: string;
    email: string;
    profileUrl?: string | null;
    color: string;
    source: string;
    platform: string;
};

interface ImportFansTableProps {
    fansImportData: ImportFansItem[];
}

const ImportFansTable = ({
    fansImportData
}: ImportFansTableProps) => {
    const classes = useStyles();

    /** Transform column data into TableElement format */
    const columnDefs = useMemo(() => {
        if (!fansImportData.length) {
            return []
        }

        /** Get unique keys from all data items to determine table columns */
        const allKeys = Array.from(new Set(fansImportData.flatMap(item => Object.keys(item))));
        const hiddenFields = ['color', 'profileUrl', 'ip_address', 'mergeSuccessful'];

        let filteredColumns = allKeys.filter(key => {
            if (hiddenFields.includes(key)) return false;
            return fansImportData.some(item => {
                const value = item[key as keyof ImportFansItem];
                return value !== null && value !== undefined && value !== '';
            });
        });

        /** Ensure email is first column */
        const emailIndex = filteredColumns.indexOf('email');
        if (emailIndex !== -1) {
            filteredColumns.splice(emailIndex, 1);
            filteredColumns.unshift('email');
        }

        /** Transform into ColDef array */
        const columns: ColDef[] = filteredColumns.map(field => ({
            field,
            title: field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1'),
            renderCell: field === 'email' ?
                (row: ImportFansItem) => (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {row.profileUrl ?
                            <Avatar className={classes.avatar} src={row.profileUrl} />
                            :
                            <div
                                className={clsx('avatarGenericGansTableImport', classes.avatar)}
                                style={{ backgroundColor: row.color }}
                            />
                        }
                        <div>{row.email}</div>
                    </div>
                )
                :
                undefined
        }));

        return columns;
    }, [fansImportData, classes.avatar]);

    return (
        <TableElement
            className="importFansTableTable"
            rowData={fansImportData}
            columnDefs={columnDefs}
            pagination
            paginationPageSize={25}
            paginationPageSizeSelector={[10, 25, 50]}
        />
    );
};

export default ImportFansTable;
